import { Component, type ErrorInfo, type ReactNode } from "react";
import { log } from "@/lib/log";
import { Button } from "@/components/ui/Button";

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error("React error boundary caught", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div
          style={{
            display: "flex",
            minHeight: "100vh",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 24,
            textAlign: "center",
            background: "var(--bg)",
            color: "var(--ink-1)",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>Etwas ist schiefgelaufen.</h1>
          <p style={{ maxWidth: 480, fontSize: 14, color: "var(--ink-3)" }}>
            {this.state.error.message || "Ein unerwarteter Fehler ist aufgetreten."}
          </p>
          <Button onClick={this.reset} variant="primary">
            Neu laden
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
